import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import {
  Music,
  Play,
  Calendar,
  DollarSign,
  Upload,
  Users,
  TrendingUp,
  CheckCircle,
  ChevronRight,
  Heart,
  Link as LinkIcon,
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatCard = ({ icon: Icon, value, label }) => (
  <div className="bg-[#16181D] rounded-2xl p-5 hover:bg-[#1E2028] transition-colors">
    <div className="flex items-center gap-3 mb-2">
      <div className="h-10 w-10 rounded-full bg-[#FFE500]/10 flex items-center justify-center">
        <Icon className="h-5 w-5 text-[#FFE500]" />
      </div>
      <p className="text-sm text-[#A0A0A0]">{label}</p>
    </div>
    <p className="text-3xl font-extrabold text-white">{value}</p>
  </div>
);

const Dashboard = () => {
  const { user, token, isArtist, isVerified } = useAuth();
  const [stats, setStats] = useState(null);
  const [mySongs, setMySongs] = useState([]);
  const [myBookings, setMyBookings] = useState({ as_user: [], as_artist: [] });

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const requests = [axios.get(`${API}/bookings/my`, { headers })];
      if (isArtist) {
        requests.push(axios.get(`${API}/stats/dashboard`, { headers }));
        requests.push(axios.get(`${API}/songs?artist_id=${user.id}`, { headers }));
      }
      const responses = await Promise.all(requests);
      setMyBookings(responses[0].data);
      if (isArtist && responses[1]) setStats(responses[1].data);
      if (isArtist && responses[2]) setMySongs(responses[2].data);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    }
  };

  const upcomingBookings = [...myBookings.as_user, ...myBookings.as_artist]
    .filter((b) => b.status === 'confirmed')
    .sort((a, b) => new Date(a.date) - new Date(b.date))
    .slice(0, 5);

  return (
    <Layout>
      <div className="py-6 md:py-8 px-4 md:px-8 pb-40">
        <div className="container mx-auto max-w-7xl">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div className="flex items-center gap-4">
              <div className="h-16 w-16 rounded-full bg-[#FFE500] flex items-center justify-center text-black text-2xl font-extrabold overflow-hidden">
                {user?.profile_image ? (
                  <img src={user.profile_image} alt="" className="w-full h-full object-cover" />
                ) : (
                  user?.full_name?.charAt(0)
                )}
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-extrabold text-white">
                  Welcome, {user?.full_name?.split(' ')[0]}
                </h1>
                <p className="text-[#A0A0A0] flex items-center gap-2 mt-1 text-sm">
                  @{user?.username}
                  {isVerified && <CheckCircle className="h-4 w-4 text-[#FFE500]" />}
                  <span className="text-[11px] uppercase bg-[#16181D] border border-[#1E2028] px-2 py-0.5 rounded-full tracking-wider">
                    {user?.role}
                  </span>
                </p>
              </div>
            </div>

            {isArtist && (
              <Link to="/upload">
                <button className="pill-btn pill-btn-active flex items-center gap-2" data-testid="upload-song-btn">
                  <Upload className="h-4 w-4" />
                  Upload New Song
                </button>
              </Link>
            )}
          </div>

          {/* Verification Banner */}
          {isArtist && !isVerified && (
            <div className="bg-gradient-to-r from-[#FFE500]/15 to-transparent border border-[#FFE500]/30 rounded-2xl p-6 mb-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div className="flex items-start gap-4">
                <CheckCircle className="h-8 w-8 text-[#FFE500] flex-shrink-0" />
                <div>
                  <h3 className="text-lg font-bold text-white">Get Verified</h3>
                  <p className="text-[#A0A0A0] text-sm">
                    Unlock premium features: priority ranking, lower fees, and the verified badge.
                  </p>
                </div>
              </div>
              <Link to="/verification">
                <button className="pill-btn pill-btn-active whitespace-nowrap">Get Verified — ₹799/month</button>
              </Link>
            </div>
          )}

          {/* Stats Grid */}
          {isArtist && stats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <StatCard icon={Music} value={stats.total_songs} label="Songs" />
              <StatCard icon={Play} value={(stats.total_plays || 0).toLocaleString()} label="Plays" />
              <StatCard icon={Calendar} value={stats.upcoming_bookings} label="Bookings" />
              <StatCard icon={DollarSign} value={`₹${(stats.total_earnings || 0).toLocaleString()}`} label="Earnings" />
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main */}
            <div className="lg:col-span-2 space-y-6">
              {/* My Songs */}
              {isArtist && (
                <div className="bg-[#16181D] rounded-2xl p-6">
                  <div className="flex items-center justify-between mb-5">
                    <h2 className="text-xl font-bold text-white">My Songs</h2>
                    <Link to="/upload" className="text-sm text-[#FFE500] font-semibold flex items-center gap-1 hover:gap-2 transition-all">
                      Upload <ChevronRight className="h-4 w-4" />
                    </Link>
                  </div>
                  {mySongs.length > 0 ? (
                    <div className="space-y-2">
                      {mySongs.slice(0, 5).map((song) => (
                        <div key={song.id} className="song-row">
                          <img
                            src={song.cover_image || 'https://images.unsplash.com/photo-1581650127213-e72e2271ff15?w=100'}
                            alt={song.title}
                            className="h-12 w-12 rounded-lg object-cover"
                          />
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-white truncate">{song.title}</p>
                            <p className="text-sm text-[#A0A0A0]">{song.genre}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm text-white flex items-center gap-1">
                              <Play className="h-3 w-3" /> {song.play_count || 0}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-10 text-[#A0A0A0]">
                      <Music className="h-12 w-12 mx-auto mb-3 opacity-30" />
                      <p>No songs uploaded yet</p>
                      <Link to="/upload">
                        <button className="pill-btn pill-btn-active mt-4">Upload Your First Song</button>
                      </Link>
                    </div>
                  )}
                </div>
              )}

              {/* Upcoming Bookings */}
              <div className="bg-[#16181D] rounded-2xl p-6">
                <div className="flex items-center justify-between mb-5">
                  <h2 className="text-xl font-bold text-white">Upcoming Sessions</h2>
                  <Link to="/mentors" className="text-sm text-[#FFE500] font-semibold flex items-center gap-1">
                    Browse <ChevronRight className="h-4 w-4" />
                  </Link>
                </div>
                {upcomingBookings.length > 0 ? (
                  <div className="space-y-3">
                    {upcomingBookings.map((booking) => (
                      <div key={booking.id} className="flex items-center gap-4 p-4 bg-[#1E2028] rounded-xl border-l-4 border-[#FFE500]">
                        <div className="h-10 w-10 rounded-full bg-[#FFE500]/10 flex items-center justify-center">
                          <Calendar className="h-5 w-5 text-[#FFE500]" />
                        </div>
                        <div className="flex-1">
                          <p className="font-semibold text-white capitalize">
                            {booking.session_type?.replace('_', ' ')}
                          </p>
                          <p className="text-sm text-[#A0A0A0]">
                            with {booking.artist_id === user?.id ? booking.user_name : booking.artist_name}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-white text-sm">{booking.date}</p>
                          <p className="text-xs text-[#A0A0A0]">{booking.time}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-10 text-[#A0A0A0]">
                    <Calendar className="h-12 w-12 mx-auto mb-3 opacity-30" />
                    <p>No upcoming sessions</p>
                    <Link to="/mentors">
                      <button className="pill-btn pill-btn-inactive mt-4">Book a Session</button>
                    </Link>
                  </div>
                )}
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Quick Actions */}
              <div className="bg-[#16181D] rounded-2xl p-6">
                <h2 className="text-xl font-bold text-white mb-4">Quick Actions</h2>
                <div className="space-y-2">
                  <Link to="/discover" className="flex items-center gap-3 p-3 rounded-xl hover:bg-[#1E2028] transition-colors text-white">
                    <Music className="h-4 w-4 text-[#FFE500]" />
                    Discover Music
                  </Link>
                  <Link to="/playlists" className="flex items-center gap-3 p-3 rounded-xl hover:bg-[#1E2028] transition-colors text-white">
                    <TrendingUp className="h-4 w-4 text-[#FFE500]" />
                    Browse Playlists
                  </Link>
                  <Link to="/mentors" className="flex items-center gap-3 p-3 rounded-xl hover:bg-[#1E2028] transition-colors text-white">
                    <Users className="h-4 w-4 text-[#FFE500]" />
                    Find Mentors
                  </Link>
                  {isArtist && (
                    <>
                      <Link to={`/${user?.username}`} className="flex items-center gap-3 p-3 rounded-xl hover:bg-[#1E2028] transition-colors text-white">
                        <CheckCircle className="h-4 w-4 text-[#FFE500]" />
                        View My Profile
                      </Link>
                      <Link to="/bio-link" className="flex items-center gap-3 p-3 rounded-xl hover:bg-[#1E2028] transition-colors text-white">
                        <LinkIcon className="h-4 w-4 text-[#FFE500]" />
                        Edit Bio.Link
                      </Link>
                    </>
                  )}
                </div>
              </div>

              {/* Tip Card */}
              <div className="bg-gradient-to-br from-[#FFE500]/10 to-transparent border border-[#FFE500]/20 rounded-2xl p-6">
                <Heart className="h-8 w-8 text-[#FFE500] mb-3" />
                <h3 className="text-lg font-bold text-white mb-2">Share your music</h3>
                <p className="text-sm text-[#A0A0A0] mb-4">
                  Your Bio.Link profile is your shareable home on M4manna — add it to your socials.
                </p>
                {isArtist ? (
                  <Link to={`/${user?.username}`}>
                    <button className="pill-btn pill-btn-active text-sm">View Profile</button>
                  </Link>
                ) : (
                  <Link to="/register">
                    <button className="pill-btn pill-btn-active text-sm">Become an Artist</button>
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Dashboard;
